'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useT } from '@/lib/useT';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { Mail, Lock, ArrowRight, Shield, KeyRound } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { AuthShell, AuthCard, AuthField, AuthDivider, AuthLinks, AuthLink } from '@/components/shell/AuthShell';
import { startAuthentication } from '@simplewebauthn/browser';
import { APP_VERSION_LABEL } from '@/lib/version';

export default function LoginPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [totpTicket, setTotpTicket] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const router = useRouter();
  const { setAuth } = useAuthStore();

  const finishLogin = (data: any) => {
    setAuth(data.accessToken, data.refreshToken, data.user);
    toast.success(t('login_welcome_toast'));
    // Role-based landing — CUSTOMER got dropped on /dashboard which is the
    // org-admin/driver surface. They have their own dedicated area.
    const role = data.user?.role;
    const dest =
      role === 'SUPER_ADMIN' ? '/super-admin' :
      role === 'CUSTOMER'    ? '/customer/dashboard' :
      '/dashboard';
    router.push(dest);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api.post('/auth/login', { email, password });
      if (data.requiresTotpEnrollment) {
        // SUPER_ADMIN without 2FA must enroll — that flow lives in the admin panel.
        toast.error('This account must enable 2FA in the admin panel (admin.logixa.one) before signing in.', { duration: 8000 });
        setLoading(false);
        return;
      }
      if (data.requiresTotp && data.ticket) {
        setTotpTicket(data.ticket);
        setLoading(false);
        return;
      }
      finishLogin(data);
    } catch (err: any) {
      toast.error(err.message || t('login_invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totpCode.length < 6) return;
    setLoading(true);
    try {
      const data = await api.post('/auth/2fa/login', { ticket: totpTicket, code: totpCode });
      finishLogin(data);
    } catch (err: any) {
      toast.error(err.message || t('login_2fa_invalid'));
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Passkey (WebAuthn) login. Bypasses password entirely — user touches
   * Touch ID / Face ID / hardware key. Works only when the device has
   * already registered a passkey via the security settings page.
   */
  const handlePasskey = async () => {
    if (!('credentials' in navigator)) {
      toast.error(t('login_passkey_unsupported'));
      return;
    }
    setLoading(true);
    try {
      const options = await api.post('/auth/passkeys/login/begin', {});
      const response = await startAuthentication({ optionsJSON: options });
      const data = await api.post('/auth/passkeys/login/finish', { response });
      if (data?.requiresTotp && data.ticket) {
        setTotpTicket(data.ticket);
        return;
      }
      finishLogin(data);
    } catch (err: any) {
      // User-cancellation paths from the WebAuthn ceremony all map to
      // NotAllowedError. Spec text varies between browsers — match by
      // err.name first, then fall back to message substrings.
      const name = err?.name || '';
      const msg = (err?.message || '').toLowerCase();
      const userCancelled =
        name === 'NotAllowedError' ||
        name === 'AbortError' ||
        msg.includes('cancel') ||
        msg.includes('timed out') ||
        msg.includes('not allowed');
      if (userCancelled) return;
      toast.error(err?.message || t('login_passkey_failed'));
    } finally {
      setLoading(false);
    }
  };

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  return (
    <AuthShell
      kicker={label('login_kicker', 'Transport management system')}
      headline={label('login_headline', 'Your dispatch, fleet and paperwork in one place.')}
      lede={label('login_tagline', 'Orders, drivers, vehicles, tachograph and invoicing — one login for the whole transport operation.')}
      points={[
        label('login_point_1', 'Live map, orders and driver hours in one screen'),
        label('login_point_2', 'EU 561 compliance and tachograph downloads'),
        label('login_point_3', 'Data hosted in Germany, GDPR-compliant'),
      ]}
    >
      {!totpTicket ? (
        <>
          <AuthCard
            title={label('login_welcome', 'Welcome back')}
            subtitle={label('login_subtitle', 'Sign in to your account')}
            footer={
              <span>
                {label('login_no_account', 'No account yet?')}{' '}
                <AuthLink href="/register">{label('login_register_org', 'Register your company')}</AuthLink>
              </span>
            }
          >
            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              <AuthField label={label('login_email', 'Email address')} icon={Mail}>
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoComplete="email" placeholder="mail@unternehmen.de"
                />
              </AuthField>

              <AuthField label={label('login_password', 'Password')} icon={Lock}>
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required autoComplete="current-password" placeholder="••••••••"
                />
              </AuthField>

              <Button type="submit" variant="primary" disabled={loading} className="w-full h-11 text-[14px]" icon={loading ? undefined : ArrowRight}>
                {loading ? label('login_submit_loading', 'Signing in…') : label('login_submit', 'Sign in')}
              </Button>

              <AuthDivider>{label('login_or', 'or')}</AuthDivider>

              <Button type="button" onClick={handlePasskey} disabled={loading} className="w-full h-11 text-[14px]" icon={KeyRound}>
                {label('login_passkey', 'Sign in with a passkey')}
              </Button>
              <p className="text-[12px] text-[var(--text-muted)] text-center">
                {label('login_passkey_hint', 'Works if you added a passkey in security settings.')}
              </p>
            </form>
          </AuthCard>

          <AuthLinks>
            <AuthLink href="/forgot-password">{label('login_forgot', 'Forgot your password?')}</AuthLink>
            <span className="flex items-center gap-3">
              <AuthLink href="/register/driver">{label('login_register_driver', 'Register as a driver')}</AuthLink>
              <i className="w-px h-3 bg-[var(--border-strong)]" aria-hidden />
              <AuthLink href="/register/customer">{label('login_register_customer', 'Register as a customer')}</AuthLink>
            </span>
          </AuthLinks>
        </>
      ) : (
        <AuthCard
          title={label('login_2fa_title', 'Two-factor authentication')}
          subtitle={label('login_2fa_hint', 'Enter the six-digit code from your authenticator app.')}
        >
          <form onSubmit={handleTotp} className="flex flex-col gap-4">
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[var(--radius-sm)] bg-[var(--brand-subtle)] border border-[var(--brand-border)]">
              <Shield size={15} className="text-[var(--brand)] shrink-0" />
              <span className="text-[12.5px] text-[var(--text-secondary)]">
                {label('login_2fa_backup', 'You can also use one of your backup codes.')}
              </span>
            </div>

            <AuthField label={label('login_2fa_code', 'Verification code')}>
              <Input
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputMode="numeric" autoFocus placeholder="000000"
                className="h-11 text-center text-[20px] font-mono tracking-[0.35em]"
              />
            </AuthField>

            <Button type="submit" variant="primary" disabled={loading || totpCode.length < 6} className="w-full h-11 text-[14px]">
              {loading ? label('login_2fa_verifying', 'Verifying…') : label('login_2fa_verify', 'Verify')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setTotpTicket(null); setTotpCode(''); }} className="w-full">
              {label('login_back', 'Back')}
            </Button>
          </form>
        </AuthCard>
      )}

      <p className="mt-6 text-center font-mono text-[11px] text-[var(--text-muted)]">
        LOGIXA Web {APP_VERSION_LABEL}
      </p>
    </AuthShell>
  );
}
